import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Leaf, Plus, X, ChevronLeft, ChevronRight, Check, Ruler } from "lucide-react";
import { toast } from "sonner";
import { kgToLbs, lbsToKg, cmToFtIn, ftInToCm } from "@/lib/units";
import { MedicationFields } from "@/components/MedicationFields";
import { IngredientListInput } from "@/components/IngredientListInput";
import { normalizeAge, ageError, medicationsError, cleanMedications, profileErrorMessage } from "@/lib/profileValidation";

const STEPS = ["Basics", "Conditions", "Medications", "Allergies & Diet", "Goals"];

export default function Onboarding() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [ref, setRef] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [formError, setFormError] = useState("");

  const [form, setForm] = useState({
    age: 30,
    sex: "female",
    height_cm: 168,
    weight_kg: 70,
    activity_level: "moderate",
    conditions: [],
    medications: [],
    allergies: [],
    custom_allergies: [],
    excluded_ingredients: [],
    preferred_ingredients: [],
    dietary_preferences: [],
    goals: [],
    units: "metric",
    notes: "",
  });
  const [lbBuf, setLbBuf] = useState(String(kgToLbs(70)));
  const [ftBuf, setFtBuf] = useState(String(cmToFtIn(168).ft));
  const [inBuf, setInBuf] = useState(String(cmToFtIn(168).in));

  useEffect(() => {
    api.get("/reference").then((r) => setRef(r.data));
    api.get("/profile").then((r) => {
      if (r.data) setForm({ ...form, ...r.data });
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggle = (key, val) => {
    setForm((f) => {
      const arr = f[key];
      if (arr.includes(val)) {
        return { ...f, [key]: arr.filter((v) => v !== val) };
      }
      if (arr.length >= 10) {
        toast.error("Maximum 10 items allowed");
        return f;
      }
      return { ...f, [key]: [...arr, val] };
    });
  };

  const addMed = () => {
    if (form.medications.length >= 10) {
      toast.error("Maximum 10 medications allowed");
      return;
    }
    update({ medications: [...form.medications, { name: "", brand_name: "", dose: "", frequency: "" }] });
  };
  const updateMed = (i, patch) => {
    const meds = [...form.medications];
    meds[i] = { ...meds[i], ...patch };
    update({ medications: meds });
  };
  const removeMed = (i) => update({ medications: form.medications.filter((_, idx) => idx !== i) });

  const next = () => {
    const error = step === 0 ? ageError(form.age) : step === 2 ? medicationsError(form.medications) : "";
    setShowErrors(true);
    setFormError(error);
    if (error) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => { setFormError(""); setStep((s) => Math.max(s - 1, 0)); };

  const save = async () => {
    setShowErrors(true);
    const error = ageError(form.age) || medicationsError(form.medications);
    setFormError(error);
    if (error) { setStep(ageError(form.age) ? 0 : 2); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        age: Number(form.age),
        medications: cleanMedications(form.medications),
      };
      await api.put("/profile", payload);
      toast.success("Profile saved");
      await refresh();
      navigate("/dashboard");
    } catch (e) {
      setFormError(profileErrorMessage(e));
      toast.error(profileErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!ref) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen gradient-hero py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-sky-400 flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-bold text-lg">NutriGuide</span>
        </div>

        <Card className="rounded-3xl border-emerald-100 p-6 md:p-8 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="font-display text-2xl font-bold text-slate-900">Set up your health profile</h1>
              <p className="text-sm text-slate-500 mt-1">Step {step + 1} of {STEPS.length}: {STEPS[step]}</p>
            </div>
          </div>

          {/* Progress */}
          <div className="flex gap-2 mb-8">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-emerald-500" : "bg-emerald-100"}`} />
            ))}
          </div>

          {step === 0 && (
            <div className="space-y-5">
              <div className="flex items-center justify-end -mb-2">
                <div className="flex items-center gap-2 bg-emerald-50 rounded-full p-1">
                  <button
                    type="button"
                    onClick={() => update({ units: "metric" })}
                    data-testid="onb-units-metric"
                    className={`px-3 py-1 text-xs rounded-full transition-all ${form.units !== "imperial" ? "bg-white shadow font-semibold text-emerald-700" : "text-slate-500"}`}
                  >
                    <Ruler className="w-3 h-3 inline mr-1" /> cm / kg
                  </button>
                  <button
                    type="button"
                    onClick={() => update({ units: "imperial" })}
                    data-testid="onb-units-imperial"
                    className={`px-3 py-1 text-xs rounded-full transition-all ${form.units === "imperial" ? "bg-white shadow font-semibold text-emerald-700" : "text-slate-500"}`}
                  >
                    ft / lb
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Age</Label>
                  <Input type="text" inputMode="numeric" value={form.age}
                    onChange={(e) => { if (/^\d*$/.test(e.target.value)) update({ age: normalizeAge(e.target.value) }); }}
                    aria-invalid={showErrors && !!ageError(form.age)} className="rounded-xl mt-1.5" data-testid="onb-age" />
                </div>
                <div>
                  <Label>Sex</Label>
                  <Select value={form.sex} onValueChange={(v) => update({ sex: v })}>
                    <SelectTrigger className="rounded-xl mt-1.5" data-testid="onb-sex"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.units !== "imperial" ? (
                  <>
                    <div>
                      <Label>Height (cm)</Label>
                      <Input type="number" value={form.height_cm} onChange={(e) => update({ height_cm: +e.target.value })} className="rounded-xl mt-1.5" data-testid="onb-height" />
                    </div>
                    <div>
                      <Label>Weight (kg)</Label>
                      <Input type="number" step="0.1" value={form.weight_kg} onChange={(e) => update({ weight_kg: +e.target.value })} className="rounded-xl mt-1.5" data-testid="onb-weight" />
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label>Height (ft / in)</Label>
                      <div className="flex gap-2 mt-1.5">
                        <Input type="number" min="0" className="rounded-xl" value={ftBuf}
                          onChange={(e) => setFtBuf(e.target.value)}
                          onBlur={() => update({ height_cm: ftInToCm(ftBuf || 0, inBuf || 0) })}
                          data-testid="onb-height-ft" placeholder="ft" />
                        <Input type="number" min="0" max="11" className="rounded-xl" value={inBuf}
                          onChange={(e) => setInBuf(e.target.value)}
                          onBlur={() => update({ height_cm: ftInToCm(ftBuf || 0, inBuf || 0) })}
                          data-testid="onb-height-in" placeholder="in" />
                      </div>
                    </div>
                    <div>
                      <Label>Weight (lb)</Label>
                      <Input type="number" step="0.1" className="rounded-xl mt-1.5" value={lbBuf}
                        onChange={(e) => setLbBuf(e.target.value)}
                        onBlur={() => {
                          const n = parseFloat(lbBuf);
                          if (!isNaN(n) && n > 0) update({ weight_kg: lbsToKg(n) });
                        }}
                        data-testid="onb-weight-lb" />
                    </div>
                  </>
                )}
              </div>
              <div>
                <Label>Activity level</Label>
                <Select value={form.activity_level} onValueChange={(v) => update({ activity_level: v })}>
                  <SelectTrigger className="rounded-xl mt-1.5" data-testid="onb-activity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedentary">Sedentary (little exercise)</SelectItem>
                    <SelectItem value="light">Light (1-3 days/wk)</SelectItem>
                    <SelectItem value="moderate">Moderate (3-5 days/wk)</SelectItem>
                    <SelectItem value="active">Active (6-7 days/wk)</SelectItem>
                    <SelectItem value="very_active">Very active (hard exercise daily)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <p className="text-sm text-slate-500 mb-4">Select any conditions that apply. We&apos;ll personalize everything to these.</p>
              <div className="grid sm:grid-cols-2 gap-2.5">
                {ref.conditions.map((c) => {
                  const on = form.conditions.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggle("conditions", c.id)}
                      data-testid={`onb-condition-${c.id}`}
                      className={`text-left p-3 rounded-2xl border transition-all ${
                        on
                          ? "border-emerald-500 bg-emerald-50"
                          : "border-emerald-100 hover:border-emerald-200 bg-white"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-medium text-slate-900 text-sm">{c.name}</div>
                          <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{c.description}</div>
                        </div>
                        {on && <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Your medications</p>
                  <p className="text-sm text-slate-500">Add prescription and OTC drugs you take regularly.</p>
                </div>
                <Button type="button" onClick={addMed} variant="outline" className="rounded-full" data-testid="onb-add-med">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {form.medications.length === 0 && (
                <div className="text-center py-10 text-slate-400 text-sm border-2 border-dashed border-emerald-100 rounded-2xl">
                  No medications added yet.
                </div>
              )}
              {form.medications.map((m, i) => (
                <MedicationFields key={i} medication={m} index={i} prefix="onb"
                  onChange={(patch) => updateMed(i, patch)} onRemove={() => removeMed(i)} showErrors={showErrors} />
              ))}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <IngredientListInput label="Other allergies" values={form.custom_allergies}
                onChange={(values) => update({ custom_allergies: values })} testId="onb-custom-allergy" placeholder="e.g. pineapple" />
              <IngredientListInput label="Excluded ingredients" values={form.excluded_ingredients}
                onChange={(values) => update({ excluded_ingredients: values })} testId="onb-excluded" placeholder="e.g. cilantro, tofu (add separately)" />
              <IngredientListInput label="Preferred ingredients" values={form.preferred_ingredients}
                onChange={(values) => update({ preferred_ingredients: values })} testId="onb-preferred" placeholder="e.g. chickpeas" />
              <div>
                <p className="font-medium text-slate-900 mb-3">Allergies</p>
                <div className="flex flex-wrap gap-2">
                  {ref.common_allergies.map((a) => {
                    const on = form.allergies.includes(a);
                    return (
                      <button
                        key={a}
                        type="button"
                        onClick={() => toggle("allergies", a)}
                        data-testid={`onb-allergy-${a.replace(/\W+/g, "-")}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          on ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-100 bg-white text-slate-700"
                        }`}
                      >
                        {a}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="font-medium text-slate-900 mb-3">Dietary preferences</p>
                <div className="flex flex-wrap gap-2">
                  {ref.dietary_preferences.map((d) => {
                    const on = form.dietary_preferences.includes(d.id);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => toggle("dietary_preferences", d.id)}
                        data-testid={`onb-diet-${d.id}`}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-all ${
                          on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-emerald-100 bg-white text-slate-700"
                        }`}
                      >
                        {d.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <div>
                <p className="font-medium text-slate-900 mb-3">Your goals</p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {ref.goals.map((g) => {
                    const on = form.goals.includes(g.id);
                    return (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => toggle("goals", g.id)}
                        data-testid={`onb-goal-${g.id}`}
                        className={`p-3 rounded-2xl border text-left transition-all ${
                          on ? "border-emerald-500 bg-emerald-50" : "border-emerald-100 bg-white"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-slate-900 text-sm">{g.name}</span>
                          {on && <Check className="w-4 h-4 text-emerald-600" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Anything else? (optional)</Label>
                <Textarea
                  className="rounded-xl mt-1.5"
                  rows={4}
                  placeholder="Goals, food dislikes, recent labs…"
                  value={form.notes || ""}
                  onChange={(e) => update({ notes: e.target.value })}
                  data-testid="onb-notes"
                />
              </div>
            </div>
          )}

          {formError && <p role="alert" className="mt-5 text-sm text-red-700" data-testid="onb-validation-error">{formError}</p>}
          <div className="flex items-center justify-between mt-8">
            <Button variant="ghost" onClick={back} disabled={step === 0} className="rounded-full" data-testid="onb-back">
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={next} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="onb-next">
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={save} disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="onb-save">
                {busy ? "Saving…" : "Finish & continue"}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
